import { useCallback, useEffect, useMemo, useState } from "react";
import {
  emptyPixiAddress,
  type PixiAddress,
  type PixiPaymentMethod,
  type ShippingService,
  type StepProps,
} from "../../../hooks/usePixiWizardState";
import { DEFAULT_HARDWARE_CHECKOUT_COUNTRY } from "../../../shippingCountries";
import { isShippingSupported } from "../utils/supportedShippingCountries";
import {
  type AddressErrors,
  type AddressField,
  hasErrors,
  isValidEmail,
  validateAddress,
} from "../utils/validation";

export type AddressFormKind = "billing" | "shipping";

/** Contact fields copied from billing into shipping when still useful. */
const CONTACT_FIELDS: AddressField[] = [
  "firstName",
  "secondName",
  "company",
  "email",
  "phone",
];

const touchKey = (form: AddressFormKind, field: AddressField): string =>
  `${form}.${field}`;

export interface UsePaymentShippingForm {
  paymentMethod: PixiPaymentMethod;
  billing: PixiAddress;
  /** Shipping values to display (contact may be seeded from billing). */
  shipping: PixiAddress;
  useSameAddress: boolean;
  showShippingForm: boolean;
  /**
   * Whether the shipping address fields are read-only. True when "same address"
   * is ticked and the billing country is shippable: the card stays visible with
   * billing values mirrored in, but only the shipping-method dropdown is
   * editable. The unsupported-billing case keeps the fields editable.
   */
  shippingDisabled: boolean;
  showUnsupportedWarning: boolean;
  /** Whether the same-address toggle is shown (hidden when no hardware ships). */
  showSameAddressToggle: boolean;
  /** Selected hardware shipping service (defaults to "standard"). */
  shippingService: ShippingService;
  unsupportedCountryName: string;
  isValid: boolean;
  setPaymentMethod: (method: PixiPaymentMethod) => void;
  setShippingService: (service: ShippingService) => void;
  updateBilling: (field: AddressField, value: string) => void;
  updateShipping: (field: AddressField, value: string) => void;
  setUseSameAddress: (checked: boolean) => void;
  setCardPaymentMethodId: (id: string | null) => void;
  markTouched: (form: AddressFormKind, field: AddressField) => void;
  fieldError: (form: AddressFormKind, field: AddressField) => boolean;
}

/**
 * Owns the Payment & Shipping step's form state and derivations: payment
 * method selection, billing/shipping addresses, the same-address toggle, the
 * unsupported-shipping-country case, field validation and the overall validity
 * reported up to the wizard footer.
 *
 * Billing and shipping are stored separately so manual shipping entries are
 * preserved across same-address toggles. While same-address is checked the
 * effective shipping address is billing (computed, not written over the manual
 * entry), unless billing is in an unsupported shipping country — in which case
 * the shipping form is shown with contact info seeded from billing but address
 * fields left blank.
 */
export const usePaymentShippingForm = ({
  state,
  updateState,
  countryName,
}: StepProps & {
  countryName: (countryCode: string) => string;
}): UsePaymentShippingForm => {
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const paymentMethod: PixiPaymentMethod = state.paymentMethod ?? "credit_card";
  const shippingService: ShippingService = state.shippingService ?? "standard";
  const useSameAddress = state.useSameAddressForShipping ?? false;

  const billing = useMemo(
    () => state.billingAddress ?? emptyPixiAddress(),
    [state.billingAddress],
  );
  // Preselect the shipping country from the deal step's "Ship to" choice
  // (GRWBKK-1667). The deal card always displays a resolved country — the
  // user's pick or the default — so mirror that here for the two screens to
  // agree. Seed only while the form country is still blank; once the user sets
  // a country (or edits any shipping field) the stored value wins, keeping it
  // editable and preserving manual entries.
  const dealShippingCountry =
    state.shippingCountry ?? DEFAULT_HARDWARE_CHECKOUT_COUNTRY;
  const manualShipping = useMemo(() => {
    const stored = state.shippingAddress ?? emptyPixiAddress();
    if (stored.country.trim()) return stored;
    return { ...stored, country: dealShippingCountry };
  }, [state.shippingAddress, dealShippingCountry]);

  const billingCountrySet = !!billing.country.trim();
  const billingShippable = isShippingSupported(billing.country);
  const billingUnsupported = billingCountrySet && !billingShippable;

  // The "discount" deal ships no hardware, so there is no shipping at all:
  // hide the shipping form, the same-address toggle and the warning entirely.
  const needsShipping = state.dealId !== "discount";

  // The shipping card is shown whenever hardware ships. When "same address" is
  // ticked and the billing country is shippable, the fields mirror billing and
  // render read-only (the shipping-method dropdown stays editable). When billing
  // is in an unsupported country the fields stay editable so the user can enter
  // a supported shipping address.
  const showShippingForm = needsShipping;
  const shippingDisabled =
    needsShipping && useSameAddress && !billingUnsupported;
  const showUnsupportedWarning =
    needsShipping && useSameAddress && billingUnsupported;

  /**
   * Shipping values to display. When same-address mirrors a shippable billing
   * address, show billing (read-only). In the unsupported same-address case,
   * seed the contact section from billing while keeping address fields blank —
   * the billing address must not be copied.
   */
  const shipping = useMemo<PixiAddress>(() => {
    if (shippingDisabled) return billing;
    if (!showUnsupportedWarning) return manualShipping;
    const seeded = { ...manualShipping };
    CONTACT_FIELDS.forEach((field) => {
      if (!seeded[field].trim()) seeded[field] = billing[field];
    });
    return seeded;
  }, [shippingDisabled, showUnsupportedWarning, manualShipping, billing]);

  const billingErrors: AddressErrors = useMemo(
    () =>
      validateAddress(billing, {
        requireState: true,
        requireShippingSupported: false,
      }),
    [billing],
  );

  const shippingErrors: AddressErrors = useMemo(
    () =>
      validateAddress(shipping, {
        requireState: false,
        requireShippingSupported: true,
      }),
    [shipping],
  );

  // Credit card needs a tokenized paymentMethod.id (held by Stripe). Every other
  // method (Invoice, PayPal, Direct Debit) has no step-3 input to validate — its
  // gateway operation happens at step 4 (Checkout).
  const cardTokenized = !!state.cardPaymentMethodId;
  const paymentValid = paymentMethod !== "credit_card" || cardTokenized;
  const billingValid = !hasErrors(billingErrors);
  // No hardware shipping, or shipping mirrors a valid billing address → nothing
  // extra to validate; otherwise the editable shipping fields must be valid.
  const shippingValid =
    !needsShipping || shippingDisabled || !hasErrors(shippingErrors);
  const isValid = paymentValid && billingValid && shippingValid;

  // Report validity up so the wizard footer can gate Continue.
  useEffect(() => {
    if (state.paymentShippingValid !== isValid) {
      updateState({ paymentShippingValid: isValid });
    }
  }, [isValid, state.paymentShippingValid, updateState]);

  const setPaymentMethod = useCallback(
    (method: PixiPaymentMethod) => updateState({ paymentMethod: method }),
    [updateState],
  );

  const setShippingService = useCallback(
    (service: ShippingService) => updateState({ shippingService: service }),
    [updateState],
  );

  const updateBilling = useCallback(
    (field: AddressField, value: string) =>
      updateState({ billingAddress: { ...billing, [field]: value } }),
    [billing, updateState],
  );

  const updateShipping = useCallback(
    (field: AddressField, value: string) =>
      updateState({ shippingAddress: { ...shipping, [field]: value } }),
    [shipping, updateState],
  );

  const setUseSameAddress = useCallback(
    (checked: boolean) => updateState({ useSameAddressForShipping: checked }),
    [updateState],
  );

  const setCardPaymentMethodId = useCallback(
    (id: string | null) =>
      updateState({ cardPaymentMethodId: id ?? undefined }),
    [updateState],
  );

  const markTouched = useCallback(
    (form: AddressFormKind, field: AddressField) =>
      setTouched((prev) => ({ ...prev, [touchKey(form, field)]: true })),
    [],
  );

  const fieldError = useCallback(
    (form: AddressFormKind, field: AddressField): boolean => {
      // Read-only shipping fields mirror validated billing; no errors shown.
      if (form === "shipping" && shippingDisabled) return false;
      const errors = form === "billing" ? billingErrors : shippingErrors;
      if (!errors[field]) return false;
      const values = form === "billing" ? billing : shipping;
      // Bad email format surfaces immediately, as in the design.
      if (
        field === "email" &&
        values.email.trim() &&
        !isValidEmail(values.email)
      ) {
        return true;
      }
      return !!touched[touchKey(form, field)];
    },
    [
      billingErrors,
      shippingErrors,
      billing,
      shipping,
      touched,
      shippingDisabled,
    ],
  );

  return {
    paymentMethod,
    billing,
    shipping,
    useSameAddress,
    showShippingForm,
    shippingDisabled,
    showUnsupportedWarning,
    showSameAddressToggle: needsShipping,
    shippingService,
    unsupportedCountryName: billingUnsupported
      ? countryName(billing.country)
      : "",
    isValid,
    setPaymentMethod,
    setShippingService,
    updateBilling,
    updateShipping,
    setUseSameAddress,
    setCardPaymentMethodId,
    markTouched,
    fieldError,
  };
};
